import { ArticlePreviewView, GrowthDashboardTopBarActions } from '@elva/ui';
import type { ArArticleDetail } from '@elva/ui';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useNotify } from '@/data/notifications';
import { findArticleIdByKeyword, sectionsToMarkdown } from '../api';
import { useArticleMutations } from '../hooks/useArticles';

function articleIdFromCreateResponse(job: Record<string, unknown>): string {
  return String(
    job.article_id ?? job.articleId ?? job.entity_id ?? job.entityId ?? job.id ?? '',
  ).trim();
}

export function ArticleCreatePage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const { draft, save } = useArticleMutations();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const handleSave = useCallback(
    async (article: ArArticleDetail) => {
      const keyword = article.targetKeyword.trim();
      if (!keyword) {
        notify.error('Target keyword is required');
        return;
      }

      setSaving(true);
      try {
        const job = await draft.mutateAsync({ targetKeyword: keyword });
        let articleId = articleIdFromCreateResponse(job as Record<string, unknown>);
        if (!articleId) {
          articleId = (await findArticleIdByKeyword(keyword)) ?? '';
        }
        if (!articleId) {
          notify.error('Draft queued, but article id was not returned yet. Check Content hub.');
          navigate('/content');
          return;
        }

        const bodyMarkdown = sectionsToMarkdown(article.sections, article.images);
        const title = article.title.trim() || article.meta.title.trim();
        const metaTitle = article.meta.title.trim();
        const metaDescription = article.meta.description.trim();
        const metaSlug = article.meta.slug.trim();
        const hasMeta = Boolean(metaTitle || metaDescription || metaSlug);

        await save.mutateAsync({
          id: articleId,
          ...(title ? { title } : {}),
          targetKeyword: keyword,
          ...(bodyMarkdown ? { bodyMarkdown } : {}),
          ...(hasMeta
            ? {
                meta: {
                  ...(metaTitle ? { title: metaTitle } : {}),
                  ...(metaDescription ? { description: metaDescription } : {}),
                  ...(metaSlug ? { slug: metaSlug } : {}),
                },
              }
            : {}),
        });

        navigate(`/articles/${articleId}`);
      } catch (error) {
        notify.error(error, 'Could not create article');
      } finally {
        setSaving(false);
      }
    },
    [draft, navigate, notify, save],
  );

  return (
    <ArticlePreviewView
      {...viewProps({
        mode: 'create' as const,
        saving: saving || draft.isPending || save.isPending,
        onBack: () => navigate('/content'),
        onSave: handleSave,
      })}
    />
  );
}
